import type {
  Collection,
  Direction,
  KeyboardDelegate,
  Orientation,
} from '@react-types/shared';
import type {Key} from 'react';

export class TabsKeyboardDelegate<T> implements KeyboardDelegate {
  private collection: Collection<T>;

  private flipDirection: boolean;

  private disabledKeys: Set<Key>;

  constructor(
    collection: Collection<T>,
    direction: Direction,
    orientation: Orientation,
    disabledKeys: Set<Key> = new Set(),
  ) {
    this.collection = collection;
    this.flipDirection = direction === 'rtl' && orientation === 'horizontal';
    this.disabledKeys = disabledKeys;
  }

  getKeyLeftOf(key: Key) {
    if (this.flipDirection) {
      return this.getNextKey(key);
    }
    return this.getPreviousKey(key);
  }

  getKeyRightOf(key: Key) {
    if (this.flipDirection) {
      return this.getPreviousKey(key);
    }
    return this.getNextKey(key);
  }

  getKeyAbove(key: Key) {
    return this.getPreviousKey(key);
  }

  getKeyBelow(key: Key) {
    return this.getNextKey(key);
  }

  getFirstKey() {
    let key = this.collection.getFirstKey();
    if (key && this.disabledKeys.has(key)) {
      key = this.getNextKey(key);
    }
    return key;
  }

  getLastKey() {
    let key = this.collection.getLastKey();
    if (key && this.disabledKeys.has(key)) {
      key = this.getPreviousKey(key);
    }
    return key;
  }

  getNextKey(key: Key) {
    let next: Key | null = key;
    do {
      next = this.collection.getKeyAfter(next);
      if (next == null) {
        next = this.collection.getFirstKey();
      }
    } while (next && this.disabledKeys.has(next));
    return next;
  }

  getPreviousKey(key: Key) {
    let next: Key | null = key;
    do {
      next = this.collection.getKeyBefore(next);
      if (next == null) {
        next = this.collection.getLastKey();
      }
    } while (next && this.disabledKeys.has(next));
    return next;
  }
}
